<template>
    <v-dialog
        v-model="showDialog"
        persistent
        eager
        :width="dialogWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card ref="dialogCard">
            <v-card-title
                class="py-2 px-4"
                role="region"
                aria-label="dialog-title"
            >
                <v-container pa-0 fluid>
                    <v-row no-gutters>
                        <v-col>
                            <span>{{ dialogTitle }}</span>
                            <help-button :href="helpUrl"></help-button>
                        </v-col>
                        <v-col v-if="hasServiceAccount">
                            <service-account-info
                                :id="serviceAccount.xboAccountId"
                                :name="serviceAccount.name"
                                id-label="XBO Account Id"
                            ></service-account-info>
                        </v-col>
                    </v-row>
                    <v-row v-if="formErrorMessage.length" no-gutters>
                        <v-col class="px-2 text-body-1 error--text">
                            {{ formErrorMessage }}
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-title>
            <v-card-text class="py-1 px-6">
                <div v-show="isExistingRecord && !isFormEditing">
                    <space-read-form
                        :value="spaceData"
                        role="form"
                        aria-label="space-read-form"
                    ></space-read-form>
                </div>
                <v-expand-transition mode="in-out">
                    <v-form
                        v-show="isNewRecord || isFormEditing"
                        ref="form"
                        v-model="isFormValid"
                        role="form"
                        aria-label="space-edit-form"
                    >
                        <space-edit-form
                            :value="spaceEditData"
                            :has-service-account="hasServiceAccount"
                            :visibility="spaceEditData.visibility"
                            :selected-venue-info="selectedVenueInfo"
                            @input="onEditFormChange"
                        ></space-edit-form>
                    </v-form>
                </v-expand-transition>
                <v-card elevation="10">
                    <v-card-title
                        class="px-4 pt-1 pb-0"
                        role="region"
                        aria-label="space-devices-header"
                    >
                        <div class="text-subtitle-2">
                            <span>{{ spaceDevicesTitle }}</span>
                            <span
                                v-show="isNewRecord || isFormEditing"
                                class="pl-2"
                            >
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            ref="addDevices"
                                            outlined
                                            x-small
                                            color="accent"
                                            v-on="on"
                                            @click="onAddDeviceClick"
                                        >
                                            Add devices
                                        </v-btn>
                                    </template>
                                    <span>Add devices to this space</span>
                                </v-tooltip>
                            </span>
                        </div>
                    </v-card-title>
                    <v-card-text
                        class="px-4 pb-3"
                        role="region"
                        aria-label="space-devices-list"
                    >
                        <space-devices-list
                            :items="spaceDevices"
                            :is-editing="isNewRecord || isFormEditing"
                            @remove="onRemoveDeviceClick"
                        ></space-devices-list>
                    </v-card-text>
                </v-card>
            </v-card-text>
            <v-card-actions
                class="px-6 py-3"
                role="region"
                aria-label="dialog-actions"
            >
                <v-row no-gutters>
                    <v-col cols="auto">
                        <v-btn
                            v-if="showDelete"
                            outlined
                            small
                            color="error"
                            :loading="isDeleteLoading"
                            :disabled="!allowDeleteButton"
                            @click="onDeleteClick"
                            >Delete</v-btn
                        >
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-if="showUpload"
                                    outlined
                                    small
                                    color="accent"
                                    :disabled="!allowUploadButton"
                                    v-on="on"
                                    @click="onUploadClick"
                                    >File Upload</v-btn
                                >
                            </template>
                            <span>Upload spaces from CSV file</span>
                        </v-tooltip>
                    </v-col>
                    <v-col class="text-center">
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-if="showReset"
                                    outlined
                                    small
                                    color="accent"
                                    class="mx-1"
                                    :loading="isResetLoading"
                                    :disabled="!allowResetButton"
                                    v-on="on"
                                    @click="onResetClick"
                                    >Reset</v-btn
                                >
                            </template>
                            <span>Reset all devices in this room</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-if="showCheckin"
                                    outlined
                                    small
                                    color="accent"
                                    class="mx-1"
                                    :loading="isCheckOutLoading"
                                    :disabled="!allowCheckinButton"
                                    v-on="on"
                                    @click="onCheckOutClick"
                                    >Check Out</v-btn
                                >
                            </template>
                            <span>Check out this room</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-if="showCheckout"
                                    outlined
                                    small
                                    color="accent"
                                    class="mx-1"
                                    :loading="isCheckInLoading"
                                    :disabled="!allowCheckoutButton"
                                    v-on="on"
                                    @click="onCheckInClick"
                                    >Check In</v-btn
                                >
                            </template>
                            <span>Check in this room</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-if="showTvListings"
                                    outlined
                                    small
                                    color="accent"
                                    class="mx-1"
                                    :loading="isTvListingsLoading"
                                    :disabled="!allowTvListingsButton"
                                    v-on="on"
                                    @click="onRemoteTunerClick"
                                    >Remote Tuner</v-btn
                                >
                            </template>
                            <span
                                >Show TV listings and remote tune into a tv
                                channel</span
                            >
                        </v-tooltip>
                    </v-col>
                    <v-col class="text-right" cols="auto">
                        <v-btn
                            v-if="showClose"
                            outlined
                            small
                            @click="onCloseClick"
                            >Close</v-btn
                        >
                        <v-btn
                            v-if="showCancel"
                            outlined
                            small
                            @click="onCancelClick"
                            >Cancel</v-btn
                        >
                        <v-btn
                            v-if="showEdit"
                            color="primary"
                            class="ml-2"
                            small
                            :disabled="!allowEditButton"
                            @click="onEditClick"
                            >Edit</v-btn
                        >
                        <v-btn
                            v-if="showSave"
                            color="primary"
                            class="ml-2"
                            small
                            :loading="isSaveLoading"
                            :disabled="!allowSaveButton"
                            @click="onSaveClick"
                            >Save</v-btn
                        >
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
        <device-selection-dialog
            ref="deviceSelection"
        ></device-selection-dialog>
        <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
        <component
            :is="tvListingsComponent"
            :show="showTvListingsDialog"
            :service-account="serviceAccount"
            :channel-map="assignedChannelMap"
            @close="onCloseTvListingsDialog"
        ></component>
    </v-dialog>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { isEmpty } from '@/common/helpers';
import SpaceDevicesList from './SpaceDevicesList';
import ServiceAccountInfo from './ServiceAccountInfo';
import SpaceReadForm from '../forms/SpaceReadForm';
import SpaceEditFrom from '../forms/SpaceEditForm';
import DeviceSelectionDialog from '../../devices/components/DeviceSelectionDialog';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import TvListingsDialog from './TvListingsDialog';
import { pick } from 'lodash';
import HelpButton from '@/components/Buttons/HelpButton';

export default {
    name: 'SpaceDetailsDialog',
    components: {
        'space-devices-list': SpaceDevicesList,
        'space-read-form': SpaceReadForm,
        'space-edit-form': SpaceEditFrom,
        'device-selection-dialog': DeviceSelectionDialog,
        'confirmation-dialog': ConfirmationDialog,
        'service-account-info': ServiceAccountInfo,
        'tv-listings-dialog': TvListingsDialog,
        'help-button': HelpButton
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true
        },
        spaceId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            isFormValid: false,
            isFormDirty: false,
            isDevicesDirty: false,
            isFormEditing: false,
            spaceEditData: {},
            // the newly added devices are stored here until we call the save
            addedDevices: [],
            formErrorMessage: '',
            // the buttons loading variables
            isSaveLoading: false,
            isDeleteLoading: false,
            isResetLoading: false,
            isCheckInLoading: false,
            isCheckOutLoading: false,
            isTvListingsLoading: false,
            tvListingsComponent: '',
            showTvListingsDialog: false
        };
    },
    computed: {
        ...mapState('space', {
            selectedSpace: 'space',
            serviceAccount: 'serviceAccount'
        }),
        ...mapState('space', ['readHelpUrl', 'editHelpUrl', 'addHelpUrl']),
        ...mapState('channelMaps', ['channelMaps']),
        ...mapState('venue', ['selectedVenueInfo']),
        ...mapGetters('venue', ['currentMso']),
        isNewRecord() {
            return !this.spaceId.length;
        },
        isExistingRecord() {
            return this.spaceId.length;
        },
        allowEditButton() {
            return this.$can('edit', 'Space');
        },
        allowSaveButton() {
            return (
                (this.isFormDirty || this.isDevicesDirty) &&
                this.isFormValid &&
                !this.isSaveLoading
            );
        },
        allowDeleteButton() {
            return this.$can('delete', 'Space') && !this.isDeleteLoading;
        },
        allowUploadButton() {
            return this.$can('create', 'Space');
        },
        allowResetButton() {
            return (
                this.$can('control', 'Space') &&
                !this.isResetLoading &&
                this.hasServiceAccount
            );
        },
        allowCheckoutButton() {
            return (
                this.$can('control', 'Space') &&
                !this.isCheckOutLoading &&
                this.hasServiceAccount
            );
        },
        allowCheckinButton() {
            return (
                this.$can('control', 'Space') &&
                !this.isCheckInLoading &&
                this.hasServiceAccount
            );
        },
        allowTvListingsButton() {
            return (
                this.$can('tune', 'Remote') &&
                !this.isTvListingsLoading &&
                this.hasServiceAccount
            );
        },
        showEdit() {
            return this.isExistingRecord && !this.isFormEditing;
        },
        showSave() {
            return this.isFormEditing || this.isNewRecord;
        },
        showCancel() {
            return this.isFormEditing || this.isNewRecord;
        },
        showClose() {
            return this.isExistingRecord && !this.isFormEditing;
        },
        showDelete() {
            return (
                this.isExistingRecord &&
                this.isFormEditing &&
                this.$can('show', 'Buttons/spaces/deleteSpaces')
            );
        },
        showUpload() {
            return (
                this.isNewRecord &&
                this.$can('show', 'Buttons/spaces/uploadSpaces')
            );
        },
        showReset() {
            return (
                this.isExistingRecord &&
                !this.isFormEditing &&
                this.$can('show', 'Buttons/spaces/resetAccount')
            );
        },
        showCheckin() {
            return (
                this.isExistingRecord &&
                !this.isFormEditing &&
                this.$can('show', 'Buttons/spaces/checkInOut')
            );
        },
        showCheckout() {
            return (
                this.isExistingRecord &&
                !this.isFormEditing &&
                this.$can('show', 'Buttons/spaces/checkInOut')
            );
        },
        showTvListings() {
            return (
                this.isExistingRecord &&
                !this.isFormEditing &&
                this.$can('show', 'Buttons/spaces/tvListings')
            );
        },
        dialogTitle() {
            if (this.isNewRecord) {
                return 'Add space';
            } else if (this.isExistingRecord && this.isFormEditing) {
                return 'Edit space';
            } else {
                return 'Space';
            }
        },
        spaceDevicesTitle() {
            if (this.spaceDevices && this.spaceDevices.length > 0) {
                return `Space devices (${this.spaceDevices.length})`;
            } else {
                return 'Space devices';
            }
        },
        dialogWidth() {
            if (this.$vuetify.breakpoint.sm) {
                return '95%';
            } else {
                return '950';
            }
        },
        hasServiceAccount() {
            return !isEmpty(this.serviceAccount);
        },
        hasDevices() {
            return !isEmpty(this.spaceDevices);
        },
        assignedChannelMap() {
            let channelMapId = '';
            let channelMapName = '(Unassigned - will use default)';
            let visibility = '';
            if (this.serviceAccount && this.serviceAccount.venueChannelMapId) {
                channelMapId = this.parseChannelMapFullId(
                    this.serviceAccount.venueChannelMapId
                );
            }
            if (channelMapId && this.channelMaps) {
                const foundChannelMap = this.channelMaps.find(
                    item => item.id === channelMapId
                );
                if (foundChannelMap) {
                    channelMapName =
                        foundChannelMap.displayName ||
                        foundChannelMap.locationName;
                    visibility = foundChannelMap.visibility;
                }
            }
            return {
                id: channelMapId,
                name: channelMapName,
                visibility
            };
        },
        spaceData() {
            return {
                ...this.selectedSpace,
                channelMapName: this.assignedChannelMap.name,
                channelMapId: this.assignedChannelMap.id,
                status: this.occupancyStatus
            };
        },
        spaceDevices() {
            let existingDevices = [];
            if (this.hasServiceAccount) {
                existingDevices = this.serviceAccount.devices.map(device => {
                    return {
                        ...device,
                        disableRemove: true
                    };
                });
            }
            return [...existingDevices, ...this.addedDevices];
        },
        defaultPublicChannelMapId() {
            if (this.selectedVenueInfo) {
                return this.selectedVenueInfo.defaultPublicChannelMapId;
            }
            return {};
        },
        defaultPrivateChannelMapId() {
            if (this.selectedVenueInfo) {
                return this.selectedVenueInfo.defaultPrivateChannelMapId;
            }
            return {};
        },
        helpUrl() {
            if (this.isNewRecord) {
                return this.addHelpUrl;
            } else if (this.isFormEditing) {
                return this.editHelpUrl;
            }
            return this.readHelpUrl;
        },
        occupancyStatus() {
            if (this.selectedSpace && this.selectedSpace.occupiedStatus) {
                return `${
                    this.selectedSpace.occupiedStatus
                } @ ${this.$options.filters.localeDateTime(
                    this.selectedSpace.occupiedStatusUpdatedOn
                )}`;
            }
            return '';
        }
    },
    watch: {
        spaceId: {
            immediate: true,
            handler(value) {
                if (value === '') {
                    this.spaceEditData = {
                        channelMapName: this.assignedChannelMap.name
                    };
                }
            }
        },
        serviceAccount() {
            this.copySpaceEditData();
        },
        selectedSpace() {
            this.copySpaceEditData();
        }
    },
    created() {
        this.fetchSpaceData();
    },
    methods: {
        ...mapActions('space', [
            'fetchSpace',
            'resetSpace',
            'updateSpaceEntity',
            'createSpaceEntity',
            'deleteSpace',
            'checkIsSpaceUnique',
            'resetAccount',
            'checkInSpace',
            'checkOutSpace'
        ]),
        // ========================== Events ==============================
        onCloseClick() {
            this.closeThisDialog();
        },
        onCancelClick() {
            if (this.isFormDirty || this.isDevicesDirty) {
                // need to confirm cancel changes
                this.$refs.confirmationDialog
                    .open(
                        'Edit Space',
                        'Are you sure, you want to close without saving',
                        'Form data has been modified',
                        {
                            confirmActionText: 'Save change & close',
                            cancelActionText: 'Cancel changes',
                            showIcon: true
                        }
                    )
                    .then(res => {
                        if (res === true) {
                            this.saveThisSpace();
                        } else {
                            if (this.isNewRecord) {
                                this.closeThisDialog();
                            } else {
                                // clear the changes...
                                this.resetDialogData();
                            }
                        }
                    });
            } else if (this.isExistingRecord) {
                this.isFormEditing = false;
            } else {
                this.closeThisDialog();
            }
        },
        onEditClick() {
            // editable fields are name, devices
            // building, wing, floor, room are editable only if no service account
            this.isFormEditing = true;
            this.setEditFormFocus();
        },
        onSaveClick() {
            this.saveThisSpace();
        },
        onDeleteClick() {
            const deviceBrickMessage = `Deleting a space attached to a Service Account will render all devices in this space unusable. And it will need to be returned to ${this.currentMso}.`;
            this.$refs.confirmationDialog
                .open(
                    'Delete Space',
                    'Are you sure, you want to delete this space?',
                    this.hasServiceAccount && this.hasDevices
                        ? deviceBrickMessage
                        : '',
                    {
                        confirmActionText: 'Yes, delete space',
                        cancelActionText: 'Nevermind',
                        showIcon: true,
                        subMessageClass: 'error--text'
                    }
                )
                .then(res => {
                    if (res === true) {
                        this.deleteThisSpace();
                    }
                });
        },
        onAddDeviceClick() {
            const excludeRows = this.spaceDevices.map(
                item => item.serialNumber
            );
            this.$refs.deviceSelection
                .open('Select a device', excludeRows)
                .then(res => {
                    if (res && res.length) {
                        // need to add this device to the devices list
                        this.addedDevices.push(...res);
                        this.isDevicesDirty = true;
                    }
                    // need to bring back the focus here...
                    this.setDeviceListFocus();
                });
        },
        onResetClick() {
            try {
                this.isResetLoading = true;
                this.resetAccount({
                    deviceIds: this.spaceDevices.map(item => item.id)
                });
            } finally {
                setTimeout(() => {
                    this.isResetLoading = false;
                }, 1000);
            }
        },
        onCheckInClick() {
            try {
                this.isCheckInLoading = true;
                this.checkInSpace({ spaceId: this.selectedSpace.id });
            } finally {
                setTimeout(() => {
                    this.isCheckInLoading = false;
                }, 1000);
            }
        },
        onCheckOutClick() {
            try {
                this.isCheckOutLoading = true;
                this.checkOutSpace({ spaceId: this.selectedSpace.id });
            } finally {
                setTimeout(() => {
                    this.isCheckOutLoading = false;
                }, 1000);
            }
        },
        onUploadClick() {
            setTimeout(() => {
                this.$emit('upload');
            }, 200);
            this.closeThisDialog();
        },
        onEditFormChange(value) {
            let isVisibilityDirty =
                this.spaceEditData.visibility !== value.visibility;
            this.spaceEditData = { ...value };
            this.isFormDirty = true;
            if (isVisibilityDirty && this.hasServiceAccount) {
                this.updateAssignedChannelMap(value);
            }
        },
        onRemoveDeviceClick(device) {
            if (device) {
                this.addedDevices = this.addedDevices.filter(
                    item => item.id !== device.id
                );
                this.isDevicesDirty = true;
            }
        },
        onEscapeKeydown() {
            if (this.showCancel) {
                this.onCancelClick();
            } else if (this.showClose) {
                this.onCloseClick();
            }
        },
        onRemoteTunerClick() {
            this.showTvListingsDialog = true;
            this.tvListingsComponent = 'tv-listings-dialog';
        },
        onCloseTvListingsDialog() {
            this.showTvListingsDialog = false;
            this.tvListingsComponent = '';
        },
        // ========================== Others =============================
        async closeThisDialog(refreshParentData = false, timeout = 200) {
            setTimeout(() => {
                this.resetDialogData();
                this.resetSpace();
                this.$emit('close-dialog', refreshParentData);
            }, timeout);
        },
        resetDialogData() {
            // this.$refs.form.resetValidation();
            if (this.isFormDirty) {
                this.copySpaceEditData();
            }
            this.isFormDirty = false;
            this.isDevicesDirty = false;
            this.isFormEditing = false;
            this.formErrorMessage = '';
            this.isSaveLoading = false;
            this.isDeleteLoading = false;
            this.isResetLoading = false;
            this.isCheckInLoading = false;
            this.isCheckOutLoading = false;
            this.addedDevices = [];
            this.checkVisibilityMismatch();
            this.setReadFormFocus();
        },
        async fetchSpaceData() {
            if (this.isExistingRecord) {
                await this.fetchSpace(this.spaceId);
                this.checkVisibilityMismatch();
            }
        },
        copySpaceEditData() {
            this.spaceEditData = {
                ...pick(this.selectedSpace, [
                    'id',
                    'ownerId',
                    'name',
                    'visibility',
                    'status',
                    'building',
                    'wing',
                    'floor',
                    'room',
                    'tags'
                ]),
                channelMapId: this.assignedChannelMap.id
            };
            setTimeout(() => {
                this.isFormDirty = false;
            }, 100);
        },
        parseChannelMapFullId(channelMapFullId) {
            const parts = channelMapFullId.split(/\//);
            if (parts.slice(-2, -1)[0] === 'VenueChannelMap') {
                return parts.slice(-1)[0];
            }
            return 'Invalid';
        },
        async saveThisSpace() {
            try {
                this.isSaveLoading = true;
                if (
                    this.isExistingRecord &&
                    (this.isFormDirty || this.isDevicesDirty)
                ) {
                    const isValid = await this.isFormDataValid();
                    if (isValid) {
                        const payload = {
                            oldSpace: {
                                ...this.selectedSpace,
                                tags: this.selectedSpace.tags,
                                devices: this.serviceAccount.devices || []
                            },
                            newSpace: {
                                ...this.spaceEditData,
                                tags: this.spaceEditData.tags,
                                devices: this.spaceDevices
                            },
                            oldChannelMapId: this.assignedChannelMap.id,
                            newChannelMapId:
                                this.spaceEditData.channelMapId || ''
                        };
                        await this.updateSpaceEntity(payload);
                        this.closeThisDialog(true);
                    }
                } else if (
                    this.isNewRecord &&
                    (this.isFormDirty || this.isDevicesDirty)
                ) {
                    const isValid = await this.isFormDataValid();
                    if (isValid) {
                        // need to call the api to create new space
                        const payload = {
                            space: this.spaceEditData,
                            tags: this.spaceEditData.tags || [],
                            devices: this.spaceDevices || [],
                            channelMapId: this.spaceEditData.channelMapId || ''
                        };
                        await this.createSpaceEntity(payload);
                        this.closeThisDialog(true);
                    }
                }
            } finally {
                this.isSaveLoading = false;
            }
        },
        async deleteThisSpace() {
            if (this.isExistingRecord) {
                try {
                    this.isDeleteLoading = true;
                    await this.deleteSpace({ space: this.selectedSpace });
                } finally {
                    this.isDeleteLoading = false;
                    this.closeThisDialog(true);
                }
            }
        },
        checkVisibilityMismatch() {
            if (this.assignedChannelMap.id) {
                if (
                    this.spaceData.visibility !==
                    this.assignedChannelMap.visibility
                ) {
                    this.formErrorMessage = `Space visibility [${this.spaceData.visibility}] does not match with channel linup visibility [${this.assignedChannelMap.visibility}]. Please edit and fix this issue.`;
                }
            }
        },
        updateAssignedChannelMap(data) {
            // need to change the assigned channel map to match the new visibility
            let newChannelMapId = '';
            this.formErrorMessage =
                'New channel lineup has been assigned to match the selected visibility: ' +
                data.visibility;
            if (
                this.spaceEditData.visibility === 'PRIVATE' &&
                this.defaultPrivateChannelMapId
            ) {
                newChannelMapId = this.defaultPrivateChannelMapId;
            } else if (
                this.spaceEditData.visibility === 'PUBLIC' &&
                this.defaultPublicChannelMapId
            ) {
                newChannelMapId = this.defaultPublicChannelMapId;
            }
            this.$set(this.spaceEditData, 'channelMapId', newChannelMapId);
        },
        async isFormDataValid() {
            const isValid = this.$refs.form.validate();
            const { id, building, wing, floor, room } = this.spaceEditData;
            const isUnique = await this.checkIsSpaceUnique({
                id,
                building,
                wing,
                floor,
                room,
                isNewRecord: this.isNewRecord
            });
            if (!isValid) {
                this.formErrorMessage =
                    'Please fix the highlighted fields below';
            }
            if (!isUnique) {
                this.formErrorMessage =
                    'Space already exists on this floor! Please enter a unique combination of Building, Wing, Floor, Number.';
            }
            if (!isValid || !isUnique) {
                return false;
            } else {
                this.formErrorMessage = '';
                return true;
            }
        },
        setEditFormFocus() {
            this.$nextTick(() => {
                const form = this.$refs.form;
                if (form) {
                    form.$el
                        .querySelector('.v-input input:not([disabled])')
                        .focus();
                }
            });
        },
        setReadFormFocus() {
            this.$nextTick(() => {
                const dialogCard = this.$refs.dialogCard;
                if (dialogCard) {
                    dialogCard.$el
                        .querySelector(
                            '.v-btn:not([disabled]):not([style*="display: none"])'
                        )
                        .focus();
                }
            });
        },
        setDeviceListFocus() {
            this.$nextTick(() => {
                const addButton = this.$refs.addDevices;
                addButton.$el.focus();
            });
        }
    }
};
</script>

<style lang="scss" scoped></style>
